import { Link, useNavigate } from 'react-router';
import { AlertOctagon, AlertTriangle, Bell, Eye, Inbox, ListTodo, Plus, Star } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, EmptyState } from '@vit/ui';
import { POLL_ACTIVE, useApiQuery } from '../../lib/api.js';
import { useTranslation } from '../../lib/i18n.js';
import { useMe } from '../../lib/session.js';
import { KeyLabel, PhaseBadge, PriorityLabel, RequestStatusBadge } from '../../components/badges.js';
import { QueryState, TimeAgo } from '../../components/common.js';

function Stat({ label, value, icon: Icon, to, tone }: { label: string; value: number | undefined; icon: typeof Inbox; to: string; tone: string }) {
  return (
    <Link to={to} className="group rounded-[var(--radius-lg)] border border-border bg-surface p-4 shadow-xs hover:border-border-strong">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-fg-muted">{label}</span>
        <Icon className={`size-4 ${tone}`} />
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value ?? '–'}</div>
    </Link>
  );
}

/** Personal dashboard: assigned work, authored requests, attention items and blockers. */
export function MyWorkPage() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const assigned = useApiQuery('listWorkItems', { query: { assigneeId: 'me', phase: 'TODO,IN_PROGRESS,IN_REVIEW,INTEGRATED', sort: 'priority', limit: '20' } }, { refetchInterval: POLL_ACTIVE });
  const myRequests = useApiQuery('listRequests', { query: { view: 'mine', limit: '8' } }, { refetchInterval: POLL_ACTIVE });
  const attention = useApiQuery('listRequests', { query: { view: 'attention', limit: '8' } }, { refetchInterval: POLL_ACTIVE, enabled: me.permissions.canTriage });
  const inbox = useApiQuery('listRequests', { query: { view: 'inbox', limit: '8' } }, { refetchInterval: POLL_ACTIVE, enabled: me.permissions.canTriage });
  const reviews = useApiQuery('listWorkItems', { query: { phase: 'IN_REVIEW', limit: '50' } }, { refetchInterval: POLL_ACTIVE });
  const followedWork = useApiQuery('listWorkItems', { query: { following: 'true', limit: '6' } }, { refetchInterval: POLL_ACTIVE });
  const followedRequests = useApiQuery('listRequests', { query: { view: 'following', limit: '6' } }, { refetchInterval: POLL_ACTIVE });
  const updates = useApiQuery('listNotifications', { query: { limit: '6' } }, { refetchInterval: POLL_ACTIVE });
  const items = assigned.data?.data ?? [];
  const blocked = items.filter((i) => i.blocked);
  const needsMine = (myRequests.data?.data ?? []).filter((r) => r.status === 'NEEDS_INFORMATION' || r.status === 'RESOLVED');

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('myWork.greeting', { name: me.displayName.split(' ')[0] })}</h1>
          <p className="text-sm text-fg-muted">{t('myWork.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/requests/new')}>
            <Plus /> {t('nav.newRequest')}
          </Button>
          <Button variant="primary" onClick={() => navigate('/work?create=1')}>
            <Plus /> {t('nav.newWork')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label={t('myWork.stats.open')} value={items.length} icon={ListTodo} to="/work?assigneeId=me" tone="text-info" />
        <Stat label={t('myWork.stats.inReview')} value={reviews.data?.data.filter((w) => w.assignee?.id === me.id || w.owner?.id === me.id).length} icon={Eye} to="/work?phase=IN_REVIEW" tone="text-warning" />
        <Stat label={t('myWork.stats.blocked')} value={blocked.length} icon={AlertOctagon} to="/work?assigneeId=me&blocked=true" tone="text-danger" />
        {me.permissions.canTriage ? <Stat label={t('myWork.stats.attention')} value={attention.data?.data.length} icon={AlertTriangle} to="/requests?view=attention" tone="text-danger" /> : <Stat label={t('myWork.myRequests')} value={myRequests.data?.data.length} icon={Inbox} to="/requests?view=mine" tone="text-accent" />}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card>
          <CardHeader>
            <CardTitle>{t('myWork.assigned')}</CardTitle>
            <Link to="/work?assigneeId=me" className="text-xs text-accent hover:underline">
              {t('common.all')} →
            </Link>
          </CardHeader>
          <QueryState query={assigned} empty={items.length === 0 ? <EmptyState icon={<ListTodo />} title={t('myWork.assignedEmpty')} /> : undefined}>
            <ul className="divide-y divide-border">
              {items.map((w) => (
                <li key={w.id}>
                  <Link to={`/work?selected=${w.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-2">
                    <PriorityLabel priority={w.priority} compact />
                    <KeyLabel value={w.key} />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{w.title}</span>
                    {w.blocked ? <AlertOctagon className="size-3.5 text-danger" aria-label={t('myWork.blocked')} /> : null}
                    <PhaseBadge phase={w.phase} />
                  </Link>
                </li>
              ))}
            </ul>
          </QueryState>
        </Card>

        <div className="flex flex-col gap-6">
          {needsMine.length ? (
            <Card className="border-accent/40">
              <CardHeader>
                <CardTitle>{t('myWork.needsMe')}</CardTitle>
              </CardHeader>
              <ul className="divide-y divide-border">
                {needsMine.map((r) => (
                  <li key={r.id}>
                    <Link to={`/requests/${r.id}`} className="flex items-center gap-2 px-4 py-2.5 hover:bg-surface-2">
                      <KeyLabel value={r.key} />
                      <span className="min-w-0 flex-1 truncate text-sm">{r.title}</span>
                      <RequestStatusBadge status={r.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
          {me.permissions.canTriage ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('myWork.triageInbox')}</CardTitle>
                <Link to="/requests?view=inbox" className="text-xs text-accent hover:underline">
                  {t('common.all')} →
                </Link>
              </CardHeader>
              <QueryState query={inbox} empty={inbox.data?.data.length === 0 ? <CardBody className="text-sm text-fg-subtle">{t('requests.emptyInbox')}</CardBody> : undefined}>
                <ul className="divide-y divide-border">
                  {(inbox.data?.data ?? []).map((r) => (
                    <li key={r.id}>
                      <Link to={`/requests?selected=${r.id}`} className="flex items-center gap-2 px-4 py-2.5 hover:bg-surface-2">
                        <KeyLabel value={r.key} />
                        <span className="min-w-0 flex-1 truncate text-sm">{r.title}</span>
                        <RequestStatusBadge status={r.status} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </QueryState>
            </Card>
          ) : null}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1.5">
                <Bell className="size-4 text-fg-subtle" /> {t('myWork.updates')}
              </CardTitle>
              <Link to="/settings?tab=notifications" className="text-xs text-accent hover:underline">
                {t('common.all')} →
              </Link>
            </CardHeader>
            <QueryState query={updates} empty={updates.data?.data.length === 0 ? <CardBody className="text-sm text-fg-subtle">{t('myWork.updatesEmpty')}</CardBody> : undefined}>
              <ul className="divide-y divide-border">
                {(updates.data?.data ?? []).map((n) => (
                  <li key={n.id}>
                    <a href={n.link ?? '#'} className={`flex items-center gap-2 px-4 py-2.5 hover:bg-surface-2 ${n.readAt ? 'text-fg-muted' : 'font-medium'}`}>
                      <span className="min-w-0 flex-1 truncate text-sm">{n.title}</span>
                      <span className="hidden text-xs font-normal text-fg-subtle sm:inline">
                        <TimeAgo iso={n.createdAt} />
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </QueryState>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1.5">
                <Star className="size-4 text-fg-subtle" /> {t('myWork.following')}
              </CardTitle>
              <Link to="/requests?view=following" className="text-xs text-accent hover:underline">
                {t('common.all')} →
              </Link>
            </CardHeader>
            {(followedWork.data?.data.length ?? 0) + (followedRequests.data?.data.length ?? 0) === 0 ? (
              <CardBody className="text-sm text-fg-subtle">{t('myWork.followingEmpty')}</CardBody>
            ) : (
              <ul className="divide-y divide-border">
                {(followedWork.data?.data ?? []).map((w) => (
                  <li key={w.id}>
                    <Link to={`/work?selected=${w.id}`} className="flex items-center gap-2 px-4 py-2.5 hover:bg-surface-2">
                      <KeyLabel value={w.key} />
                      <span className="min-w-0 flex-1 truncate text-sm">{w.title}</span>
                      <PhaseBadge phase={w.phase} />
                    </Link>
                  </li>
                ))}
                {(followedRequests.data?.data ?? []).map((r) => (
                  <li key={r.id}>
                    <Link to={`/requests/${r.id}`} className="flex items-center gap-2 px-4 py-2.5 hover:bg-surface-2">
                      <KeyLabel value={r.key} />
                      <span className="min-w-0 flex-1 truncate text-sm">{r.title || '—'}</span>
                      <RequestStatusBadge status={r.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{t('myWork.myRequests')}</CardTitle>
              <Link to="/requests?view=mine" className="text-xs text-accent hover:underline">
                {t('common.all')} →
              </Link>
            </CardHeader>
            <QueryState query={myRequests} empty={myRequests.data?.data.length === 0 ? <CardBody className="text-sm text-fg-subtle">{t('myWork.myRequestsEmpty')}</CardBody> : undefined}>
              <ul className="divide-y divide-border">
                {(myRequests.data?.data ?? []).map((r) => (
                  <li key={r.id}>
                    <Link to={r.status === 'DRAFT' ? `/requests/new?draft=${r.id}&mode=form` : `/requests/${r.id}`} className="flex items-center gap-2 px-4 py-2.5 hover:bg-surface-2">
                      <KeyLabel value={r.key} />
                      <span className="min-w-0 flex-1 truncate text-sm">{r.title || '—'}</span>
                      <RequestStatusBadge status={r.status} />
                      <span className="hidden text-xs sm:inline">
                        <TimeAgo iso={r.updatedAt} />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </QueryState>
          </Card>
        </div>
      </div>
    </div>
  );
}
